import { ProductSelector } from '@/components/flows/FlowEditor/properties/ProductSelector';
import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import { useShopifyIntegrationQuery } from '@/hooks/queries/use-integrations-query';
import type { ShopifyProduct } from '@/hooks/queries/use-shopify-products-query';
import { getProductDisplayPriceAmount } from '@/components/flows/FlowEditor/properties/product-price-utils';
import { Box, HStack, IconButton, Image, Input, Link, Text } from '@chakra-ui/react';
import {
  COLLAB_SETTLEABLE_CURRENCIES,
  type BriefCatalogProduct,
  type BriefFixedPayment,
  type BriefManualProduct,
} from '@fluenceur/types';
import { Xmark } from 'iconoir-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MoneyAmountField,
  type MoneyAmountInputVariant,
} from '../editor/sections/MoneyAmountField';
import { formatCents } from './format-money';

export interface BriefProductValue {
  catalog?: BriefCatalogProduct;
  manual?: BriefManualProduct;
}

interface BriefProductPickerProps {
  value: BriefProductValue | null;
  onChange: (value: BriefProductValue | null) => void;
  /** Blue "per-recipient" chrome for the selected-product card (compensation). */
  variableStyle?: boolean;
  /**
   * Ask for what the product is worth. Only compensation does: there the
   * product is the counterpart of a contract and the send is refused without a
   * value. The AI-inputs step merely names a product to write about, and has no
   * contract to price.
   */
  withValue?: boolean;
  /** Collection authoring owns the source switch for the entire offer. */
  catalogOnly?: boolean;
  portalled?: boolean;
  amountInputVariant?: MoneyAmountInputVariant;
}

export const toBriefCatalogProduct = (product: ShopifyProduct): BriefCatalogProduct => ({
  shopifyProductId: product.id,
  shopifyVariantId: product.variants[0]?.id ?? null,
  title: product.title,
  imageUrl: product.featuredImageUrl,
  priceInCents:
    product.variants[0]?.priceInCents ??
    (getProductDisplayPriceAmount(product) !== null
      ? Math.round((getProductDisplayPriceAmount(product) as number) * 100)
      : null),
});

/** Cents and currency as the money box speaks them, or nothing agreed yet. */
const toMoney = (amountCents: number | null, currency: string): BriefFixedPayment | null =>
  amountCents === null ? null : { amountCents, currency };

const SelectedCatalogCard = ({
  product,
  priceLabel,
  onRemove,
  variableStyle,
}: {
  product: BriefCatalogProduct;
  /** Rendered under the title; null when there is no price to state. */
  priceLabel: string | null;
  onRemove: () => void;
  variableStyle: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <HStack
      gap="2.5"
      px="12px"
      py="8px"
      rounded="lg"
      borderWidth="1px"
      {...(variableStyle
        ? { borderStyle: 'dashed', borderColor: 'perRecipient.border', bg: 'perRecipient.surface' }
        : { borderColor: 'cardBorder', bg: 'background' })}
    >
      {product.imageUrl ? (
        <Image
          src={product.imageUrl}
          alt={product.title}
          boxSize="28px"
          objectFit="cover"
          rounded="sm"
          flexShrink={0}
        />
      ) : (
        <Box boxSize="28px" bg="bg.subtle" rounded="sm" flexShrink={0} />
      )}
      <Box flex="1" minW="0">
        <Text
          fontSize="12.5px"
          fontWeight="semibold"
          color={variableStyle ? 'perRecipient.text' : 'dark'}
          lineClamp={1}
        >
          {product.title}
        </Text>
        {/* A worth, not a fee: quiet line under the product, never a figure of
            its own. The creator's side reads it the same way. */}
        {priceLabel && (
          <Text fontSize="11.5px" color="fg.muted" data-testid="catalog-product-price">
            {t('collabs.briefTemplates.product.valueSummary', { value: priceLabel })}
          </Text>
        )}
      </Box>
      <IconButton
        aria-label={t('collabs.briefTemplates.product.removeProduct')}
        variant="ghost"
        size="2xs"
        onClick={onRemove}
      >
        <Xmark width={14} height={14} />
      </IconButton>
    </HStack>
  );
};

/**
 * Product picker shared by the AI inputs step and the gifted-product
 * compensation. Uses the Shopify catalog combobox when the store is connected,
 * otherwise a free-text name field. Emits a normalized catalog/manual value.
 *
 * Both modes carry a value, because a gifted product is the counterpart of the
 * contract and the send is refused without one. The catalog fills it on its
 * own; the box only appears where the catalog left nothing, and once offered it
 * stays for as long as the product does — a condition read on the live price
 * would take the box away on the first digit typed into it.
 */
export const BriefProductPicker = ({
  value,
  onChange,
  variableStyle = false,
  withValue = false,
  catalogOnly = false,
  portalled = true,
  amountInputVariant,
}: BriefProductPickerProps) => {
  const { t, i18n } = useTranslation();
  const { data: shopify } = useShopifyIntegrationQuery();
  const { data: brand } = useCurrentBrandQuery();
  const hasCatalog = Boolean(shopify);

  // Nothing is paid for a gifted product, so the settleable-currency list has
  // no say here. The brand's own currency is the only one its catalog means
  // anything in; the fallback covers a brand that never set one.
  const brandCurrency = brand?.currency ?? COLLAB_SETTLEABLE_CURRENCIES[0];
  const currencies = [brandCurrency];

  // `null` follows the catalog availability (async): manual only while no
  // catalog is connected. A non-null value is the brand's explicit choice, so
  // a late-resolving Shopify query cannot yank the mode out from under them.
  const [manualOverride, setManualOverride] = useState<boolean | null>(value?.manual ? true : null);
  const manualMode = manualOverride ?? !hasCatalog;

  const selectedCatalog = value?.catalog ?? null;

  // A catalog that priced nothing, or priced at zero: zero is not a value the
  // contract can state, and the send refuses it, so the box has to open on both
  // — a refusal with no control to answer it is a dead end.
  const catalogPriceMissing = (product: BriefCatalogProduct) =>
    product.priceInCents === null || product.priceInCents <= 0;

  // The product whose catalog price was missing when it was selected. Frozen on
  // the id rather than re-read from the price, so filling the box does not
  // remove the box.
  const [pricelessProductId, setPricelessProductId] = useState<string | null>(() =>
    selectedCatalog && catalogPriceMissing(selectedCatalog)
      ? selectedCatalog.shopifyProductId
      : null
  );
  if (
    selectedCatalog &&
    catalogPriceMissing(selectedCatalog) &&
    pricelessProductId !== selectedCatalog.shopifyProductId
  ) {
    setPricelessProductId(selectedCatalog.shopifyProductId);
  }
  const catalogNeedsValue =
    withValue &&
    selectedCatalog !== null &&
    pricelessProductId === selectedCatalog.shopifyProductId;

  const valueNote = t('collabs.briefTemplates.product.valueHelp');

  const setCatalogValue = (money: BriefFixedPayment | null) => {
    if (!selectedCatalog) return;
    // The currency is not sent: the server stamps the brand's own at send time.
    onChange({ catalog: { ...selectedCatalog, priceInCents: money?.amountCents ?? null } });
  };

  const setManualValue = (money: BriefFixedPayment | null) =>
    onChange({
      manual: {
        name: value?.manual?.name ?? '',
        valueCents: money?.amountCents ?? null,
        currency: money?.currency ?? null,
      },
    });

  if (catalogOnly || (hasCatalog && !manualMode)) {
    return (
      <Box>
        {selectedCatalog ? (
          <>
            <SelectedCatalogCard
              product={selectedCatalog}
              priceLabel={
                withValue && !catalogNeedsValue && selectedCatalog.priceInCents !== null
                  ? formatCents(selectedCatalog.priceInCents, brandCurrency, i18n.language)
                  : null
              }
              onRemove={() => onChange(null)}
              variableStyle={variableStyle}
            />
            {catalogNeedsValue && (
              <Box mt="2">
                <MoneyAmountField
                  amountInputVariant={amountInputVariant}
                  value={toMoney(selectedCatalog.priceInCents, brandCurrency)}
                  onChange={setCatalogValue}
                  hasError={false}
                  currencies={currencies}
                  note={valueNote}
                  placeholder={t('collabs.briefTemplates.product.valuePlaceholder')}
                  testIdPrefix="product-value"
                />
              </Box>
            )}
          </>
        ) : (
          <ProductSelector
            portalled={portalled}
            singleSelect
            selectedProductIds={[]}
            onToggle={(product) => onChange({ catalog: toBriefCatalogProduct(product) })}
          />
        )}
        {!catalogOnly && (
          <Link
            fontSize="11.5px"
            color="fg.muted"
            mt="2"
            textDecoration="underline"
            _hover={{ color: 'dark' }}
            onClick={() => {
              setManualOverride(true);
              onChange(null);
            }}
          >
            {t('collabs.briefTemplates.product.switchToManual')}
          </Link>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Input
        variant="outline"
        placeholder={t('collabs.briefTemplates.product.manualPlaceholder')}
        value={value?.manual?.name ?? ''}
        onChange={(e) =>
          onChange(
            e.target.value
              ? {
                  manual: {
                    name: e.target.value,
                    valueCents: value?.manual?.valueCents ?? null,
                    currency: value?.manual?.currency ?? null,
                  },
                }
              : null
          )
        }
      />
      {withValue && (
        <Box mt="2">
          <Text fontSize="12px" fontWeight="semibold" color="fg.muted" mb="1.5">
            {t('collabs.briefTemplates.product.valueLabel')}
          </Text>
          <MoneyAmountField
            amountInputVariant={amountInputVariant}
            value={toMoney(
              value?.manual?.valueCents ?? null,
              value?.manual?.currency ?? brandCurrency
            )}
            onChange={setManualValue}
            hasError={false}
            currencies={currencies}
            note={valueNote}
            placeholder={t('collabs.briefTemplates.product.valuePlaceholder')}
            testIdPrefix="product-value"
          />
        </Box>
      )}
      {hasCatalog && (
        <Link
          fontSize="11.5px"
          color="fg.muted"
          mt="2"
          textDecoration="underline"
          _hover={{ color: 'dark' }}
          onClick={() => {
            setManualOverride(false);
            onChange(null);
          }}
        >
          {t('collabs.briefTemplates.product.switchToCatalog')}
        </Link>
      )}
    </Box>
  );
};
